import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowRight } from "lucide-react";
import { HERO, DUAL_BRANDS, SERVICES_SNAPSHOT } from "@/data/content";
import { HERO as HERO_TID, HOME } from "@/constants/testIds";
import StatsStrip from "@/components/site/StatsStrip";
import TestimonialsCarousel from "@/components/site/TestimonialsCarousel";
import InstagramFeed from "@/components/site/InstagramFeed";
import ArchDivider from "@/components/site/ArchDivider";
import Seo from "@/components/site/Seo";

const heroFade = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { duration: 1.4, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.45, 0.7]);

  return (
    <div>
      <Seo
        title="Indian Alchemy — Premium Event & Wedding Catering in Gurugram, Delhi NCR"
        description="Luxury event catering by Indian Alchemy and corporate catering by Platinum Hospitality. Weddings, cocktails, high teas, and 4,500+ daily corporate meals. Book a consultation."
        path="/"
        keywords="luxury event catering Gurugram, wedding catering Delhi NCR, premium catering services Gurgaon, corporate catering Gurugram, Indian Alchemy catering, catering company Udyog Vihar"
      />
      {/* HERO */}
      <section
        id="top"
        ref={heroRef}
        className="relative min-h-[100svh] w-full overflow-hidden bg-[#1A100D]"
      >
        <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
          <img
            src={HERO.bg}
            alt="Indian Alchemy signature luxury wedding catering setup — Gurugram, Delhi NCR"
            fetchpriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-[#1A100D]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A100D]/80 via-transparent to-transparent pointer-events-none" />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.9'/></svg>\")",
          }}
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="absolute top-24 md:top-28 inset-x-0 z-10"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between text-[#FDFBF6]/85">
            <span className="text-[11px] tracking-[0.3em] uppercase">{HERO.eyebrow}</span>
            <span className="hidden sm:inline text-[11px] tracking-[0.3em] uppercase">
              Est. Delhi NCR · 2001
            </span>
          </div>
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-16 md:pb-24 min-h-[100svh] flex flex-col justify-end">
          <motion.h1
            variants={heroFade}
            initial="hidden"
            animate="show"
            className="font-serif font-light text-[#FDFBF6] text-[12vw] sm:text-[8.5vw] lg:text-[6.4vw] leading-[0.98] tracking-[-0.01em] max-w-6xl"
          >
            <span className="block">{HERO.title}</span>
            <span className="block">
              <em className="italic text-[#C9A961]">{HERO.titleAccent}</em>{" "}
              {HERO.titleTail}
            </span>
          </motion.h1>

          <motion.div
            variants={heroFade}
            initial="hidden"
            animate="show"
            transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 md:mt-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8"
          >
            <p className="max-w-md text-[#FDFBF6]/85 text-base md:text-lg leading-relaxed">
              {HERO.sub}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/book"
                data-testid={HERO_TID.ctaBook}
                className="group inline-flex items-center gap-3 h-12 pl-6 pr-5 rounded-full bg-[#C9A961] text-[#3B1F1A] text-sm uppercase tracking-[0.22em] hover:bg-[#FDFBF6] transition-colors"
              >
                Book a Consultation
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#3B1F1A] text-[#C9A961] group-hover:rotate-45 transition-transform duration-500">
                  <ArrowDownRight size={16} />
                </span>
              </Link>
              <Link
                to="/portfolio"
                data-testid={HERO_TID.ctaWork}
                className="text-sm uppercase tracking-[0.22em] text-[#FDFBF6]/85 border-b border-[#FDFBF6]/40 hover:border-[#C9A961] hover:text-[#C9A961] pb-1 transition-colors"
              >
                View Our Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* DUAL BRANDS */}
      <section className="relative bg-[#FDFBF6] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex justify-center mb-16 text-[#C9A961]">
            <ArchDivider />
          </div>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">
              Two brands. One kitchen. Same family.
            </div>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl leading-[1.05] text-[#3B1F1A]">
              A hospitality ecosystem that earns <em className="italic text-[#C9A961]">trust</em> at scale and <em className="italic text-[#C9A961]">awe</em> at the table.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {DUAL_BRANDS.map((b) => (
              <motion.div
                key={b.slug}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                data-testid={b.slug === "ia" ? HOME.brandCardIA : HOME.brandCardPH}
                className={`group relative p-10 md:p-14 peach-gradient border transition-[border-color,box-shadow] duration-500 ${
                  b.tone === "ia"
                    ? "border-[#C9A961]/40 hover:border-[#C9A961] hover:shadow-[0_0_40px_-4px_rgba(201,169,97,0.55)]"
                    : "border-[#D42B2B]/25 hover:border-[#D42B2B] hover:shadow-[0_0_40px_-4px_rgba(212,43,43,0.45)]"
                }`}
              >
                <div className={`text-[11px] uppercase tracking-[0.3em] ${b.tone === "ia" ? "text-[#C9A961]" : "text-[#D42B2B]"}`}>
                  {b.kicker}
                </div>
                <h3 className="mt-4 font-serif text-3xl md:text-4xl leading-tight text-[#3B1F1A]">
                  {b.title}
                </h3>
                <p className="mt-4 text-[#3B1F1A]/75 leading-relaxed max-w-md">
                  {b.body}
                </p>
                <Link
                  to={b.href}
                  className={`mt-10 inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] pb-1 border-b transition-colors ${
                    b.tone === "ia"
                      ? "text-[#3B1F1A] border-[#C9A961] hover:text-[#C9A961]"
                      : "text-[#3B1F1A] border-[#D42B2B] hover:text-[#D42B2B]"
                  }`}
                >
                  {b.cta} <ArrowRight size={14} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES SNAPSHOT */}
      <section className="relative bg-[#FDFBF6] pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">
              What we do
            </div>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl leading-[1.05] text-[#3B1F1A] max-w-3xl">
              Eight ways we set a table.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
            {SERVICES_SNAPSHOT.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.05, duration: 0.6 }}
                className="group relative aspect-[4/5] overflow-hidden"
              >
                <img
                  src={s.img}
                  alt={`${s.title} — premium catering service by Indian Alchemy, Gurugram`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A100D]/85 via-[#1A100D]/20 to-transparent" />
                <div className="absolute inset-0 p-4 md:p-6 flex flex-col justify-end text-[#FDFBF6]">
                  <div className="font-serif text-lg md:text-2xl leading-tight">
                    {s.title}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-14 text-center">
            <Link
              to="/indian-alchemy"
              className="inline-flex items-center gap-3 h-11 px-5 rounded-full border border-[#3B1F1A]/30 text-[#3B1F1A] text-xs uppercase tracking-[0.24em] hover:bg-[#3B1F1A] hover:text-[#FDFBF6] transition-colors"
            >
              Explore all services <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <StatsStrip />
      <TestimonialsCarousel />
      <InstagramFeed />
    </div>
  );
}
